<script setup lang="ts">
import BaseButtonPrimary from '../../../base/BaseButtonPrimary.vue'
import { KeyIcon, ChatIcon, DeviceMobileIcon } from '@heroicons/vue/outline'
import LoadingSpinner from '../../../base/LoadingSpinner.vue'
import {
    getKeycloakUserActionUrl,
    KeycloakData,
    useKeycloakState,
} from '../../../../utilities/account/useKeycloakState'
import KeycloakCredentialItem from '../security/KeycloakCredentialItem.vue'
import { getExternalSecurityLink } from '../../../../utilities/usePlatformSettings'

const externalSecurityLink = getExternalSecurityLink()
const hasExternalSecurityLink = externalSecurityLink !== null
const state = useKeycloakState() as KeycloakData

const icons = {
    otp: DeviceMobileIcon,
    sms: ChatIcon,
    webauthn: KeyIcon,
}

if (!hasExternalSecurityLink) {
    state.fetchCredentials()
}
</script>

<template>
    <template v-if="hasExternalSecurityLink">
        <div class="grid grid-cols-1 gap-x-4 gap-y-8 px-4 py-5 sm:px-6">
            <div class="flex flex-col items-start gap-y-2">
                <p class="text-md font-semibold text-gray-700">
                    {{ $t('security.external_security_link.title') }}
                </p>
                <p>
                    {{ $t('security.external_security_link.hint') }}
                </p>
                <BaseButtonPrimary :href="externalSecurityLink" target="_blank">
                    {{ $t('security.external_security_link.redirect') }}
                </BaseButtonPrimary>
            </div>
        </div>
    </template>
    <template v-else>
        <div v-if="!state.credentialsLoading" class="grid grid-cols-1 gap-x-4 gap-y-8 px-4 py-5 sm:px-6">
            <div class="flex flex-col items-start gap-y-2">
                <p class="text-md font-semibold text-gray-700">
                    {{ $t('security.password.title') }}
                </p>
                <p>
                    {{ $t('security.password.hint') }}
                </p>
                <BaseButtonPrimary
                    class="mt-2 w-full md:mt-0 md:w-auto"
                    :href="`${getKeycloakUserActionUrl()}&kc_action=UPDATE_PASSWORD`">
                    {{ $t('security.password.update') }}
                </BaseButtonPrimary>
            </div>

            <div v-if="state.availableTwoFactorAuthenticationTypes.length > 0" class="grid grid-cols-1 gap-x-4 gap-y-8">
                <div class="border-t border-gray-300" />
                <div class="flex flex-col items-start gap-y-2">
                    <p class="text-md font-semibold text-gray-700">
                        {{ $t('security.two_factor_authentication.title') }}
                    </p>
                    <p>
                        {{ $t('security.two_factor_authentication.hint') }}
                    </p>

                    <div class="mt-2 flex w-full flex-row flex-wrap gap-2 md:mt-0 md:w-auto">
                        <BaseButtonPrimary
                            v-for="type in state.availableTwoFactorAuthenticationTypes"
                            :key="type.name"
                            class="w-full md:w-auto"
                            :href="`${getKeycloakUserActionUrl()}&kc_action=${type.createAction}`">
                            <Component :is="icons[type.name]" class="-ml-1 mr-2 h-6 w-6" aria-hidden="true" />
                            {{ $t(`security.two_factor_authentication.configure.${type.name}`) }}
                        </BaseButtonPrimary>
                    </div>

                    <div v-if="state.credentials.length > 0" class="relative min-w-full max-w-full">
                        <p class="mt-8 font-semibold text-gray-700">
                            {{ $t('security.two_factor_authentication.list_title') }}
                        </p>

                        <div class="mt-4 flow-root">
                            <div class="-my-2 -ml-4 -mr-8 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                <div class="inline-block min-w-full align-middle sm:px-6 lg:px-8">
                                    <table class="min-w-full table-auto divide-y divide-gray-300">
                                        <thead>
                                            <tr>
                                                <th
                                                    scope="col"
                                                    class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                                                    {{ $t('security.two_factor_authentication.type') }}
                                                </th>
                                                <th
                                                    scope="col"
                                                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                    {{ $t('security.two_factor_authentication.user_label') }}
                                                </th>
                                                <th
                                                    scope="col"
                                                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                    {{ $t('security.two_factor_authentication.date') }}
                                                </th>
                                                <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-0">
                                                    <span class="sr-only">
                                                        {{ $t('security.two_factor_authentication.action') }}
                                                    </span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody class="divide-y divide-gray-200">
                                            <KeycloakCredentialItem
                                                v-for="credential in state.credentials"
                                                :key="credential.id"
                                                :credential="credential" />
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else-if="state.credentialsLoading" class="flex h-[10vh] items-center justify-center">
            <LoadingSpinner class="h-10 w-10 fill-primary" />
        </div>
    </template>
</template>
